'use client';

import React from 'react';
import Image from 'next/image';
import styled from 'styled-components';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import Gallery from '@/components/Gallery';
import HomeElement5 from '@/components/HomeElement5';
import Link from 'next/link';
import AddToCart from '@/components/AddToCart';
import FAQSection from '@/components/FAQSection';
import { COLORS } from '@/constants/colors';
import { formatMakeName } from '@/utils/formatters';
import { BRAND_PAGE_OVERRIDES } from '@/data/brandPageOverrides';


interface Make {
  id?: number;
  title?: string;
  slug?: string;
  hero_text?: string;
  images?: Array<{ link?: string; alt?: string }>;
  models?: Array<{
    id?: number;
    title?: string;
    slug?: string;
    images?: Array<{ link?: string }>;
    products?: Array<{
      id?: number;
      gfu_part_num?: string;
      buy_url?: string;
      start_year?: string;
      end_year?: string;
      displacement?: string;
      engine?: string;
      intake?: string;
      images?: Array<{ link?: string }>;
    }>;
  }>;
}

interface BrandPageProps {
  brandName: string;
  makeData: Make | null;
}

/**
 * Brand pages can render hundreds of AddToCart instances (one per product
 * row). Mounting all of them at once means hundreds of simultaneous Shopify
 * Storefront API calls on page load. This defers a given instance's mount
 * until its wrapper is about to scroll into view, scoped locally to this
 * page rather than changing AddToCart itself (which is used sitewide).
 */
function LazyAddToCart(props: React.ComponentProps<typeof AddToCart>) {
  const [shouldRender, setShouldRender] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (shouldRender || !wrapperRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' }
    );

    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, [shouldRender]);

  // Safety net: some Shopify SDK failures (e.g. a stale buyButtonId) throw
  // inside the SDK's own internal handling rather than rejecting the promise
  // AddToCart awaits, so its onError callback never fires. The SDK always
  // creates an iframe shell regardless of success, but a failed lookup never
  // populates it, leaving it at 0 height (a successful one renders ~100px) -
  // check that instead of whether the wrapper has any children at all.
  React.useEffect(() => {
    if (!shouldRender) return;

    const timer = setTimeout(() => {
      const node = document.getElementById(`product-component-${props.id}`);
      const iframe = node?.querySelector('iframe');
      if (!iframe || iframe.offsetHeight === 0) {
        props.onError?.();
      }
    }, 4000);

    return () => clearTimeout(timer);
  }, [shouldRender, props.id, props.onError]);

  return <div ref={wrapperRef}>{shouldRender && <AddToCart {...props} />}</div>;
}

const BrandPage: React.FC<BrandPageProps> = ({ brandName, makeData }) => {
  const formattedBrand = formatMakeName(makeData?.title || brandName);
  const brandOverride = BRAND_PAGE_OVERRIDES[brandName.toLowerCase()];
  const [selectedModel, setSelectedModel] = React.useState<string>('');
  const [failedButtonIds, setFailedButtonIds] = React.useState<Set<string>>(new Set());

  const handleAddToCartError = React.useCallback((id: string) => {
    setFailedButtonIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);

  const scrollToModel = (modelSlug: string) => {
    const element = document.getElementById(`model-${modelSlug}`);
    if (element) {
      const offset = 120; // Offset for fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setSelectedModel(modelSlug);
  };

  if (!makeData) {
    return (
      <>
        <Header />
        <div className="header-gap-creator"></div>
        <Breadcrumbs items={[{ label: 'Shop by Vehicle', href: '/shop-by-vehicle' }, { label: formattedBrand }]} />
        <main className="container-0">
          <NoDataWrapper>
            <h1>Brand Not Found</h1>
            <p>We couldn't find information for {formattedBrand}.</p>
            <p>Please check the URL or <Link href="/shop-by-vehicle">browse all vehicles</Link>.</p>
          </NoDataWrapper>
        </main>
        <Footer />
      </>
    );
  }

  const validModels = (makeData.models || [])
    .filter((model): model is { id?: number; title: string; slug: string; images?: Array<{ link?: string }>; products?: any[] } =>
      Boolean(model.title && model.slug)
    );

  return (
    <>
      <Header isVehicle={true} make={{ title: formatMakeName(makeData?.title) }} models={validModels} />
      <div className="header-gap-creator"></div>
      <Breadcrumbs items={[{ label: 'Shop by Vehicle', href: '/shop-by-vehicle' }, { label: formattedBrand }]} />

      <main className="container-0">
        {/* Hero Section */}
        <HeroSection>
          <div className="hero-content">
            <h1>{brandOverride?.h1 ?? `${formattedBrand} Performance Air Filters`}</h1>
            {brandOverride?.intro && <p>{brandOverride.intro}</p>}
          </div>
        </HeroSection>

        {/* Gallery */}
        {makeData.images && makeData.images.length > 0 && (
          <Gallery
            gallery={makeData.images
              .filter(img => img.link)
              .map((img, idx) => ({
                id: idx,
                image: img.link!
              }))}
          />
        )}

        {/* Models Section */}
        {makeData.models && makeData.models.length > 0 && (
          <ModelsSection>
            <div className="titles-div">
              <div className="main-header vh-brand-main-header">
                {brandOverride?.modelsHeading ?? `Popular ${formattedBrand} Models Use Green Filter`}
              </div>
              <p className="large-paragraph gray reviews">
                {brandOverride?.modelsParagraph ?? `Below are the ${formattedBrand} models that support the application of Green Filter.`}
              </p>
            </div>

            {/* Model Selector Dropdown - Sticky */}
            {validModels.length > 1 && (
              <ModelSelectorWrapper>
                <ModelSelectorLabel>Select Model:</ModelSelectorLabel>
                <ModelSelectorDropdown
                  value={selectedModel}
                  onChange={(e) => scrollToModel(e.target.value)}
                >
                  <option value="">Select a {formattedBrand} Model</option>
                  {validModels.map((model, modelIdx) => (
                    <option key={model.id ?? `${model.slug}-${modelIdx}`} value={model.slug}>
                      {model.title}
                    </option>
                  ))}
                </ModelSelectorDropdown>
              </ModelSelectorWrapper>
            )}

            {makeData.models.map((model, idx) => (
              <ModelContainer key={model.id || idx} id={`model-${model.slug}`}>
                <div className="main-vehicle-row w-row">
                  <div className="column-4 w-col w-col-6">
                    {model.images && model.images[0]?.link && (
                      <Image
                        src={model.images[0].link}
                        alt={model.title ?? ''}
                        width={800}
                        height={600}
                        style={{ width: '100%', height: 'auto' }}
                        sizes="(max-width: 767px) 100vw, 50vw"
                        className="model-image"
                      />
                    )}
                  </div>
                  <div className="column-6 w-col w-col-6">
                    <div className="column-div left">
                      <div className="product-header">
                        {brandOverride?.modelHeadings?.[model.title ?? ''] ?? `${model.title} High Performance Air Filter`}
                      </div>
                      {model.products && model.products[0] && (
                        <>
                          <div className="product-descr">
                            Green Filter - Part{' '}
                            <Link
                              href={`/store/filter/${model.products[0].gfu_part_num}`}
                              style={{ color: COLORS.brandGreen }}
                            >
                              #{model.products[0].gfu_part_num}
                            </Link>
                          </div>
                          {model.products[0].images && model.products[0].images[0]?.link && (
                            <Image
                              src={model.products[0].images[0].link}
                              alt="filter"
                              width={500}
                              height={500}
                              style={{ width: '100%', height: 'auto' }}
                              sizes="(max-width: 767px) 100vw, 50vw"
                              className="product"
                            />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Products Table */}
                {model.products && model.products.length > 0 && (
                  <>
                    <div className="product-row top w-row">
                      <div className="w-col w-col-2 w-col-small-2 w-col-tiny-2">
                        <div className="white-div gray">
                          <div className="table-header">Year</div>
                        </div>
                      </div>
                      <div className="w-col w-col-2 w-col-small-2 w-col-tiny-2">
                        <div className="white-div gray">
                          <div className="table-header">Disp</div>
                        </div>
                      </div>
                      <div className="w-col w-col-2 w-col-small-2 w-col-tiny-2">
                        <div className="white-div gray">
                          <div className="table-header">Engine Size</div>
                        </div>
                      </div>
                      <div className="w-col w-col-2 w-col-small-2 w-col-tiny-2">
                        <div className="white-div gray">
                          <div className="table-header">Intake</div>
                        </div>
                      </div>
                      <div className="w-col w-col-4 w-col-small-4 w-col-tiny-2">
                        <div className="white-div gray">
                          <div className="table-header">Purchase</div>
                        </div>
                      </div>
                    </div>

                    {model.products.map((product, productIdx) => (
                      <div
                        className="product-row w-row"
                        key={`model-${idx}-product-${productIdx}-${product.id || ''}`}
                        onClick={(e) => {
                          // Only redirect on mobile (< 768px)
                          if (window.innerWidth < 768) {
                            // Don't redirect if clicking on the Shopify button
                            const target = e.target as HTMLElement;
                            if (!target.closest('.shopify-buy__btn')) {
                              window.location.href = `/store/filter/${product.gfu_part_num}`;
                            }
                          }
                        }}
                      >
                        <div className="w-col w-col-2 w-col-small-2 w-col-tiny-2">
                          <div className="white-div ctr">
                            <div className="product-table-text">
                              {product.start_year} - {product.end_year}
                              <br />
                              <Link
                                style={{ color: COLORS.brandGreen }}
                                href={`/store/filter/${product.gfu_part_num}`}
                              >
                                #{product.gfu_part_num}
                              </Link>
                            </div>
                          </div>
                        </div>
                        {/* Engine specs - combined for mobile, separate for desktop */}
                        <div className="w-col w-col-2 w-col-small-2 w-col-tiny-2 engine-specs-col">
                          <div className="white-div ctr">
                            <div className="product-table-text engine-specs-combined">
                              {product.displacement}  •  {product.engine}  •  {product.intake}
                            </div>
                            <div className="product-table-text engine-spec-single">{product.displacement}</div>
                          </div>
                        </div>
                        <div className="w-col w-col-2 w-col-small-2 w-col-tiny-2 engine-specs-col">
                          <div className="white-div ctr">
                            <div className="product-table-text engine-spec-single">{product.engine}</div>
                          </div>
                        </div>
                        <div className="w-col w-col-2 w-col-small-2 w-col-tiny-2 engine-specs-col">
                          <div className="white-div ctr">
                            <div className="product-table-text engine-spec-single">{product.intake}</div>
                          </div>
                        </div>
                        <div className="w-col w-col-4 w-col-small-4 w-col-tiny-2">
                          <div className="white-div ctr">
                            <div className="purchase-cell">
                              {product.buy_url && product.id && (
                                <>
                                  <div className="purchase-content">
                                    {failedButtonIds.has(`${idx}-${productIdx}-${product.id}`) ? (
                                      <PurchaseFallback>
                                        <Link href="/contact-us">Contact us for pricing</Link>
                                      </PurchaseFallback>
                                    ) : (
                                      <LazyAddToCart
                                        text="Add to Cart"
                                        buyButtonId={product.buy_url}
                                        id={`${idx}-${productIdx}-${product.id}`}
                                        background={COLORS.brandGreen}
                                        color="#fff"
                                        priceSize="20px"
                                        onError={() => handleAddToCartError(`${idx}-${productIdx}-${product.id}`)}
                                      />
                                    )}
                                  </div>
                                  {product.images && product.images[0]?.link && (
                                    <div className="product-thumb">
                                      <Image
                                        src={product.images[0].link}
                                        alt={`Product ${product.gfu_part_num}`}
                                        width={200}
                                        height={200}
                                        style={{ width: '100%', height: 'auto' }}
                                        sizes="120px"
                                      />
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Cleaning Kit Row */}
                    <div className="product-row cleaning-kit-row w-row">
                      <div className="w-col w-col-8 w-col-small-8 w-col-tiny-8">
                        <div className="white-div ctr">
                          <div className="cleaning-kit-text">Cleaning Kit #2000</div>
                        </div>
                      </div>
                      <div className="w-col w-col-4 w-col-small-4 w-col-tiny-4">
                        <div className="white-div ctr">
                          <div className="purchase-cell">
                            <div className="purchase-content">
                              {failedButtonIds.has(`cleaning-kit-${idx}`) ? (
                                <PurchaseFallback>
                                  <Link href="/contact-us">Contact us for pricing</Link>
                                </PurchaseFallback>
                              ) : (
                                <LazyAddToCart
                                  text="Add to Cart"
                                  buyButtonId="617382871076"
                                  id={`cleaning-kit-${idx}`}
                                  background={COLORS.brandGreen}
                                  color="#fff"
                                  priceSize="20px"
                                  onError={() => handleAddToCartError(`cleaning-kit-${idx}`)}
                                />
                              )}
                            </div>
                            <div className="product-thumb">
                              <Image
                                src="/assets/2000_v1_500.webp"
                                alt="Cleaning Kit #2000"
                                width={500}
                                height={500}
                                style={{ width: '100%', height: 'auto' }}
                                sizes="120px"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </ModelContainer>
            ))}
          </ModelsSection>
        )}

        {brandOverride?.faqs && (
          <FAQSection faqs={brandOverride.faqs} title={brandOverride.faqTitle} />
        )}

        <HomeElement5 />
      </main>

      <Footer />
    </>
  );
};

export default BrandPage;

const PurchaseFallback = styled.div`
  font-family: Lato, sans-serif;
  font-size: 14px;

  a {
    color: ${COLORS.brandGreen};
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }
`;

const NoDataWrapper = styled.div`
  padding: 100px 20px;
  text-align: center;
  font-family: Lato, sans-serif;

  h1 {
    font-size: 32px;
    font-weight: 700;
    color: #333;
    margin-top: 0;
    margin-bottom: 20px;
  }

  p {
    font-size: 18px;
    color: #666;
    margin-bottom: 15px;
  }

  a {
    color: ${COLORS.brandGreen};
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }
`;

const HeroSection = styled.div`
  padding: 80px 5% 60px;
  background: linear-gradient(135deg, #f5f5f5 0%, #fff 100%);
  text-align: center;

  .hero-content {
    max-width: 900px;
    margin: 0 auto;

    h1 {
      font-family: Lato, sans-serif;
      font-size: 42px;
      font-weight: 800;
      color: #1a1a1a;
      margin-top: 0;
      margin-bottom: 20px;
    }

    p {
      font-family: Lato, sans-serif;
      font-size: 18px;
      color: #555;
      line-height: 1.7;
      margin: 0;
    }
  }

  @media (max-width: 768px) {
    padding: 60px 5% 40px;

    .hero-content h1 {
      font-size: 28px;
    }

    .hero-content p {
      font-size: 16px;
    }
  }
`;

const ModelSelectorWrapper = styled.div`
  position: sticky;
  top: 120px;
  z-index: 9995;
  max-width: 600px;
  margin: 0 auto 40px;
  padding: 24px;
  background: linear-gradient(135deg, ${COLORS.brandGreen} 0%, #00c728 100%);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 173, 35, 0.2);
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    top: 110px;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 20px;
    border-radius: 0;
    max-width: 100%;
    margin: 0 0 24px 0;
  }
`;

const ModelSelectorLabel = styled.label`
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    text-align: center;
    font-size: 14px;
  }
`;

const ModelSelectorDropdown = styled.select`
  flex: 1;
  padding: 12px 40px 12px 16px;
  font-family: Lato, sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: #333;
  background: #fff;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 20px;

  &:hover {
    border-color: rgba(255, 255, 255, 0.6);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:focus {
    outline: none;
    border-color: #fff;
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.2);
  }

  option {
    padding: 12px;
    font-size: 15px;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 10px 36px 10px 14px;
  }
`;

const ModelsSection = styled.div`
  padding: 60px 5%;
  max-width: 1400px;
  margin: 0 auto;

  .titles-div {
    text-align: center;
    margin-bottom: 60px;

    .main-header {
      font-family: Lato, sans-serif;
      font-size: 32px;
      font-weight: 700;
      color: #1a1a1a;
      margin-bottom: 15px;
    }

    .vh-brand-main-header {
      font-size: 32px;
    }

    .large-paragraph {
      font-family: Lato, sans-serif;
      font-size: 17px;
      color: #666;
      line-height: 1.6;
    }

    .gray {
      color: #666;
    }

    .reviews {
      margin: 0;
    }
  }
`;

const ModelContainer = styled.div`
  margin-bottom: 80px;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

  .w-row {
    display: flex;
    flex-wrap: wrap;
  }

  .w-col {
    position: relative;
    min-height: 1px;
  }

  .w-col-6 {
    width: 50%;
  }

  .w-col-2 {
    width: 16.66666667%;
  }

  .w-col-4 {
    width: 33.33333333%;
  }

  .main-vehicle-row {
    padding: 30px;
  }

  .column-div {
    padding: 20px 0;
  }

  .column-div.left {
    text-align: left;
  }

  .product-header {
    font-family: Lato, sans-serif;
    font-size: 24px;
    font-weight: 700;
    color: #333;
    margin-bottom: 10px;
  }

  .product-descr {
    font-family: Lato, sans-serif;
    font-size: 16px;
    color: #777;
    margin-bottom: 20px;
  }

  .model-image {
    width: 100%;
    max-width: 100%;
    height: auto;
    display: block;
  }

  .product {
    width: 100%;
    max-width: 300px;
    height: auto;
    margin-top: 20px;
  }

  .product-row {
    border-top: 1px solid #e0e0e0;

    &.top {
      border-top: none;
      background: #f5f5f5;
    }

    &.cleaning-kit-row {
      background: #f9f9f9;
      border-top: 2px solid #e0e0e0;
      margin-top: 8px;
    }
  }

  .cleaning-kit-text {
    font-family: Lato, sans-serif;
    font-size: 18px;
    font-weight: 600;
    color: #333;
    text-align: left;
    padding-left: 20px;
  }

  .white-div {
    padding: 15px 10px;
    text-align: center;

    &.gray {
      background: #f5f5f5;
    }

    &.ctr {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 60px;
    }
  }

  .table-header {
    font-family: Lato, sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: #333;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .product-table-text {
    font-family: Lato, sans-serif;
    font-size: 14px;
    color: #555;
    line-height: 1.5;
  }

  .purchase-cell {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
    width: 100%;
  }

  .purchase-content {
    flex: 1;
  }

  .product-thumb {
    flex-shrink: 0;
    width: 60px;
    height: 60px;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  /* Shopify Buy Button Customization - Brand Page Override */
  .purchase-cell .shopify-buy__product {
    text-align: left !important;
  }

  /* Ultra-specific selectors to override Shopify inline styles */
  .white-div .purchase-cell div[data-component-type="product"] div[data-element="product.price"],
  .white-div .purchase-cell div[data-component-type="product"] span[data-element="product.price"],
  .white-div .purchase-cell .shopify-buy__product__price,
  .white-div .purchase-cell .shopify-buy__product__price *,
  .white-div .purchase-cell .shopify-buy__product__actual-price,
  .white-div .purchase-cell .shopify-buy__product__actual-price span {
    font-family: Lato, sans-serif !important;
    font-size: 20px !important;
    line-height: 1.4 !important;
    font-weight: 700 !important;
    color: #333 !important;
  }

  .purchase-cell .shopify-buy__product__price {
    margin-bottom: 8px !important;
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
  }

  .purchase-cell .shopify-buy__product__compare-price {
    font-family: Lato, sans-serif !important;
    font-size: 18px !important;
    font-weight: 400 !important;
    color: #999 !important;
    text-decoration: line-through !important;
  }

  .purchase-cell .shopify-buy__btn {
    font-family: Lato, sans-serif !important;
    font-size: 14px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    padding: 12px 24px !important;
    border-radius: 4px !important;
    width: auto !important;
    min-width: 140px !important;
  }

  @media (max-width: 991px) {
    .w-col-6 {
      width: 100%;
    }

    .main-vehicle-row {
      padding: 20px;
    }
  }

  /* Desktop: hide combined, show individual */
  .engine-specs-combined {
    display: none;
  }

  .engine-spec-single {
    display: block;
  }

  @media (max-width: 767px) {
    /* Mobile: show combined, hide individual */
    .engine-specs-combined {
      display: block !important;
      white-space: nowrap;
    }

    .engine-spec-single {
      display: none !important;
    }

    /* Hide 2nd and 3rd engine spec columns on mobile */
    .product-row:not(.top) .engine-specs-col:nth-child(3),
    .product-row:not(.top) .engine-specs-col:nth-child(4) {
      display: none !important;
    }

    /* Hide desktop table header */
    .product-row.top {
      display: none;
    }

    /* Clean minimal card layout */
    .product-row:not(.top) {
      position: relative;
      display: block;
      background: #fff;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      margin-bottom: 16px;
      padding: 20px 16px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        transform: translateY(-1px);
      }

      &:active {
        transform: translateY(0);
      }
    }

    .product-row:not(.top) .w-col {
      width: 100% !important;
      padding: 0;
      margin: 0;
    }

    .white-div {
      padding: 0;
      background: transparent !important;
      min-height: auto;
      display: block;
    }

    /* Year and Part Number */
    .product-row:not(.top) .w-col:nth-child(1) {
      margin-bottom: 12px;
      padding-right: 36px;
    }

    .product-row:not(.top) .w-col:nth-child(1) .product-table-text {
      font-size: 16px;
      line-height: 1.5;
      color: #1a1a1a;
      font-weight: 600;
    }

    .product-row:not(.top) .w-col:nth-child(1) .product-table-text br {
      margin-bottom: 4px;
    }

    .product-row:not(.top) .w-col:nth-child(1) a {
      font-weight: 600;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    /* Engine specs - single line on mobile */
    .product-row:not(.top) .w-col:nth-child(2) {
      margin-bottom: 16px;
      text-align: center;
    }

    .product-row:not(.top) .w-col:nth-child(2) .product-table-text {
      font-size: 14px;
      color: #666;
      white-space: nowrap;
    }

    /* Add spacing to the combined engine specs */
    .product-row:not(.top) .engine-specs-combined {
      word-spacing: 8px;
      letter-spacing: 0.3px;
    }

    /* Purchase Cell - Button and Image in Row */
    .product-row:not(.top) .purchase-cell {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 16px;
      justify-content: space-between;
    }

    .product-row:not(.top) .purchase-content {
      flex: 1;
      min-width: 0;
    }

    .product-row:not(.top) .product-thumb {
      flex-shrink: 0;
      width: 64px;
      height: 64px;
      background: #f9f9f9;
      border-radius: 8px;
      padding: 6px;
      border: 1px solid #e8e8e8;
    }

    .product-row:not(.top) .product-thumb img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    /* Price and Add to Cart */
    .product-row:not(.top) .w-col:nth-child(5) .shopify-buy__product {
      text-align: left !important;
    }

    .product-row:not(.top) .w-col:nth-child(5) .shopify-buy__product__price {
      font-size: 22px !important;
      color: ${COLORS.brandGreen} !important;
      margin-bottom: 10px !important;
      font-weight: 700 !important;
    }

    .product-row:not(.top) .w-col:nth-child(5) .shopify-buy__product__actual-price {
      font-size: 22px !important;
      color: ${COLORS.brandGreen} !important;
      font-weight: 700 !important;
    }

    .product-row:not(.top) .w-col:nth-child(5) .shopify-buy__product__compare-price {
      font-size: 16px !important;
      color: #999 !important;
      text-decoration: line-through !important;
      font-weight: 400 !important;
    }

    .product-row:not(.top) .shopify-buy__btn {
      font-size: 13px !important;
      padding: 12px 20px !important;
      min-width: 120px !important;
      white-space: nowrap !important;
      border-radius: 6px !important;
      font-weight: 700 !important;
      letter-spacing: 0.5px !important;
    }

    .product-table-text {
      font-size: 14px;
    }

    /* Cleaning Kit Row - Mobile Styling */
    .product-row.cleaning-kit-row {
      display: block;
      background: #f9f9f9;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      margin-bottom: 16px;
      margin-top: 16px;
      padding: 20px 16px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    }

    .product-row.cleaning-kit-row .w-col {
      width: 100% !important;
      padding: 0;
      margin: 0;
    }

    .product-row.cleaning-kit-row .w-col:first-child {
      margin-bottom: 16px;
    }

    .cleaning-kit-text {
      font-size: 18px;
      font-weight: 700;
      color: #333;
      text-align: center;
      padding: 0;
    }

    .product-row.cleaning-kit-row .purchase-cell {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 16px;
      justify-content: space-between;
    }

    .product-row.cleaning-kit-row .purchase-content {
      flex: 1;
      min-width: 0;
    }

    .product-row.cleaning-kit-row .product-thumb {
      flex-shrink: 0;
      width: 64px;
      height: 64px;
      background: #f9f9f9;
      border-radius: 8px;
      padding: 6px;
      border: 1px solid #e8e8e8;
    }

    .product-row.cleaning-kit-row .product-thumb img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  @media (max-width: 479px) {
    .product-row:not(.top) {
      padding: 16px 12px;
    }
  }
`;
